import { useMemo } from "react";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import { VenueBasedEventSerializer } from "@app-contexts/dashboard/features/events/data/types/events";
import { getFlattenedData } from "@shared/utils/data/data-utils";
import { InfiniteData } from "react-query";
import { PaginatorContract } from "@shared/services/api/types";
import { enums } from "@shared/constants";
import { DataProvider } from "@shared/constants/common";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import usePublicEventsService from "@app-contexts/public/features/event-detail/data/services/usePublicEventsService";
import useVenueEventsService from "@app-contexts/dashboard/features/events/data/services/events/useVenueEventsService";

export type DonationsAddonConfig = {
  /**
   * The DataProvider type - indicates which basket/events service to use
   * Example: DataProvider.Public or DataProvider.Venue
   */
  dataProvider: DataProvider;
};

export type DonationsAddonResult<
  TEvent extends PublicBaseEventSerializer | VenueBasedEventSerializer
> = {
  charityToDisplay: string | null;
  donationsEvent: TEvent | null;
  isDonationsReady: boolean;
  isBasketUpdating: boolean;
};

/**
 * Shared hook for managing donation addon logic and fetching.
 * Automatically uses the correct events service based on the provided dataProvider.
 *
 * - For DataProvider.Public/User: uses usePublicEventsService
 * - For DataProvider.Venue: uses useVenueEventsService
 *
 * @param config - Configuration with dataProvider type
 * @returns Donation addon data including charity, event, and loading states
 *
 * @example
 * // Public checkout
 * const donations = useDonationsAddon({ dataProvider: DataProvider.Public });
 *
 * @example
 * // Box office
 * const donations = useDonationsAddon({ dataProvider: DataProvider.Venue });
 */
export function useDonationsAddon<
  TEvent extends PublicBaseEventSerializer | VenueBasedEventSerializer
>({ dataProvider }: DonationsAddonConfig): DonationsAddonResult<TEvent> {
  const basketService = useBasketService();
  const { itemGroups, isBasketUpdating } = basketService;

  // Get the appropriate events service based on dataProvider
  const { usePaginated: usePublicPaginated } = usePublicEventsService();
  const { usePaginated: useVenuePaginated } = useVenueEventsService();

  const usePaginatedEvents =
    dataProvider === DataProvider.Venue
      ? useVenuePaginated
      : usePublicPaginated;

  // Get unique event IDs from basket
  const eventIds = useMemo(() => {
    const eventItemGroups = itemGroups?.filter((ig) => {
      // For public basket, check for ticket type
      // For venue basket, check for item_type
      return (
        (ig.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id ||
          ig.type !== undefined) &&
        ig.event
      );
    });

    const uniqueEventIds = Array.from(
      new Set(eventItemGroups?.map((ig) => ig.event))
    ).filter(Boolean) as number[];

    return uniqueEventIds;
  }, [itemGroups]);

  const isDonationsEnabled = eventIds.length > 0;

  // Fetch events with charity information using the selected query hook
  const { data: donationEvents, isSuccess: isDonationEventsSuccess } =
    usePaginatedEvents({
      params: {
        id__in: eventIds.join(","),
        page_size: eventIds.length,
      },
      options: {
        enabled: isDonationsEnabled,
      },
    });

  const { donationsEvent, charityToDisplay } = useMemo(() => {
    if (!donationEvents || !isDonationsEnabled) {
      return {
        donationsEvent: null,
        charityToDisplay: null,
      };
    }

    // Find the first event with charities_of_choice configured
    const flattened = getFlattenedData(
      donationEvents as InfiniteData<PaginatorContract<TEvent>>
    ) as TEvent[];

    const donationEligibleEvent = flattened.find(
      (event) =>
        event.charities_of_choice && event.charities_of_choice.length > 0
    );

    const firstCharity =
      donationEligibleEvent?.charities_of_choice?.[0] || null;

    return {
      donationsEvent: donationEligibleEvent || null,
      charityToDisplay: firstCharity,
    };
  }, [donationEvents, isDonationsEnabled]);

  const isDonationsReady = useMemo(() => {
    return (
      !isDonationsEnabled ||
      (isDonationsEnabled && isDonationEventsSuccess && !isBasketUpdating)
    );
  }, [isDonationsEnabled, isDonationEventsSuccess, isBasketUpdating]);

  return {
    charityToDisplay,
    donationsEvent,
    isDonationsReady,
    isBasketUpdating,
  };
}
